<script lang="ts">
    import {getSearchFieldTypeFiltersContextIfExists, hasAnyFilterSearchFieldDefinition} from 'src/features/search/kp-search-field-type-filter';
    import type {FieldTypeId} from 'src/typings';
    import {getLogger} from 'src/core';
    import type {Readable} from 'svelte/store';
    import type {Column, Row, Table} from '@tanstack/svelte-table';
    import type {GridField, HierarchicalRecordRow} from 'src/features/record-grid/lib/types';
    import type {FieldTypeDefinition, FondFieldTypeDefinitions} from 'src/features/record/field-types/types';
    import type {RecordGridDataManager} from 'src/features/record-grid/lib/record-grid-data-manager';
    import type {CellCoordinates} from 'shared/ui-widgets/grid/types';
    import {exists, isNull, isNullOrUndefined} from 'shared/utils/custom-utils';
    import {getFieldTypeDefinitionRecursively} from 'src/features/record/field-types/utils';
    import {hasOriginRecordReference, hasTargetRecordReference, isErrorGridFieldValue} from 'src/features/record-grid/lib/types-utils';
    import {CONTROL_COLUMN_TYPES_SET} from 'src/features/record-grid/lib/constants';
    import KpContextMenuButton from 'shared/components/kp-context-menu/KpContextMenuButton.svelte';
    import KpHorizontalSeparator from 'shared/ui-widgets/separator/KpHorizontalSeparator.svelte';
    import {extractCoordinates, getCellValueByCoordinates, getColumnByIndex, getRowByIndex, tryToFindGridCell, tryToFindRowByElement} from 'shared/ui-widgets/grid/utils';

    export let contextElementRef: Element;
    export let recordGridDataManager: RecordGridDataManager;
    export let table$: Readable<Table<HierarchicalRecordRow>>;
    export let fieldTypeDefinitions: FondFieldTypeDefinitions;

    const filteringContext = getSearchFieldTypeFiltersContextIfExists();
    const logger = getLogger();

    function isControlCell(): boolean {
        const {column} = getCellRowAndColumn();
        return CONTROL_COLUMN_TYPES_SET.includes(column.id);
    }

    function isEditableCell(): boolean {
        const {row, fieldTypeDefinition} = getCellRowAndColumn();
        return exists(fieldTypeDefinition)
            && (fieldTypeDefinition.editable.fondedValues[row.original.fond.id] ?? fieldTypeDefinition.editable.defaultValue)
            && (row.original.editPermission.allowed || (row.original.editLockStatus.locked && (row.original.editLockStatus.cause?.conflictingFieldTypeIds ?? []).includes(fieldTypeDefinition.id)))
    }

    function isRegenerableCell(): boolean {
        const {row, fieldTypeDefinition} = getCellRowAndColumn();
        return exists(fieldTypeDefinition)
            && (fieldTypeDefinition.regenerable.fondedValues[row.original.fond.id] ?? fieldTypeDefinition.regenerable.defaultValue)
            && row.original.editPermission.allowed;
    }

    function isEnabledCell(): boolean {
        const {row, fieldTypeDefinition} = getCellRowAndColumn();
        return exists(fieldTypeDefinition)
            && (fieldTypeDefinition.enabled.fondedValues[row.original.fond.id] ?? fieldTypeDefinition.enabled.defaultValue);
    }

    function isDeletableRow(): boolean {
        const row = tryToFindRowByElement(contextElementRef, $table$);
        return exists(row) && row.original.deletePermission.allowed;
    }

    function isFilterableByCellValue(): boolean {
        if (!exists(filteringContext)) {
            return false;
        }

        const {fieldTypeDefinition} = getCellRowAndColumn();
        return exists(fieldTypeDefinition) && hasAnyFilterSearchFieldDefinition(fieldTypeDefinition);
    }

    function getCellRowAndColumn(): {
        row: Row<HierarchicalRecordRow>,
        column: Column<HierarchicalRecordRow>,
        fieldTypeDefinition: FieldTypeDefinition,
        cellCoordinates: CellCoordinates
    } {
        const cellElement = tryToFindGridCell(contextElementRef);
        const coordinates = extractCoordinates(cellElement);
        const column = getColumnByIndex($table$, coordinates.columnIndex);
        const row = getRowByIndex($table$, coordinates.rowIndex);
        const fieldTypeDefinition = getFieldTypeDefinitionRecursively(fieldTypeDefinitions, column.id as FieldTypeId);
        return {row, column, fieldTypeDefinition, cellCoordinates: coordinates};
    }

    const handleDuplicateRow = () => {
        const row = tryToFindRowByElement(contextElementRef, $table$);
        if (!isNull(row)) {
            recordGridDataManager.duplicateRow(row);
        }
    };

    const handleDeleteRow = () => {
        const row = tryToFindRowByElement(contextElementRef, $table$);
        if (!isNull(row)) {
            recordGridDataManager.deleteRow(row);
        }
    };

    const handleCopyCell = () => {
        const cellElement = tryToFindGridCell(contextElementRef);
        if (!isNullOrUndefined(cellElement)) {
            recordGridDataManager.copyCell();
        }
    };

    const handlePasteCell = () => {
        const cellElement = tryToFindGridCell(contextElementRef);
        if (!isNullOrUndefined(cellElement)) {
            recordGridDataManager.pasteCell(extractCoordinates(cellElement));
        }
    };

    const handleCutCell = () => {
        const cellElement = tryToFindGridCell(contextElementRef);
        if (!isNullOrUndefined(cellElement)) {
            recordGridDataManager.cutCell(extractCoordinates(cellElement));
        }
    };

    const handleDeleteCell = () => {
        const cellElement = tryToFindGridCell(contextElementRef);
        if (!isNullOrUndefined(cellElement)) {
            recordGridDataManager.deleteCell(extractCoordinates(cellElement));
        }
    };

    const handleRegenerateCell = () => {
        const cellElement = tryToFindGridCell(contextElementRef);
        if (!isNullOrUndefined(cellElement)) {
            recordGridDataManager.regenerateCell(extractCoordinates(cellElement));
        }
    };

    const handleFilterByCellValue = () => {
        if (!exists(filteringContext)) {
            return;
        }

        const {fieldTypeDefinition, cellCoordinates} = getCellRowAndColumn();
        if (!hasAnyFilterSearchFieldDefinition(fieldTypeDefinition)) {
            return;
        }

        try {
            const values = getCellValueByCoordinates<HierarchicalRecordRow, GridField[]>($table$, cellCoordinates);
            const value = values[0];
            if (!exists(value) || isErrorGridFieldValue(value)) {
                return;
            }

            if (exists(fieldTypeDefinition.originReferenceFilterSearchDefinition) && hasOriginRecordReference(value)) {
                filteringContext.setFilteredValue(fieldTypeDefinition.originReferenceFilterSearchDefinition.fieldName, {
                    val: value.originRecordReference,
                    datatype: fieldTypeDefinition.originReferenceFilterSearchDefinition.datatype.name
                });
                return;
            }

            if (exists(fieldTypeDefinition.targetReferenceFilterSearchDefinition) && hasTargetRecordReference(value)) {
                filteringContext.setFilteredValue(fieldTypeDefinition.targetReferenceFilterSearchDefinition.fieldName, {
                    val: value.targetRecordReference,
                    datatype: fieldTypeDefinition.targetReferenceFilterSearchDefinition.datatype.name
                });
                return;
            }

            if (exists(fieldTypeDefinition.valueFilterSearchDefinition) && ('value' in value || 'id' in value)) {
                filteringContext.setFilteredValue(fieldTypeDefinition.valueFilterSearchDefinition.fieldName, {
                    val: 'id' in value ? value.id : value.value,
                    datatype: fieldTypeDefinition.valueFilterSearchDefinition.datatype.name
                });
                return;
            }

            logger.warn('Cannot filter by cell value', value);
        } catch (e) {
            logger.warn('Cannot get cell value by coordinates', cellCoordinates, e);
        }
    };

    const controlCell = isControlCell();
    const enabledCell = isEnabledCell();
    const editableCell = isEditableCell();
    const regenerableCell = isRegenerableCell();
    const deletableRow = isDeletableRow();
    const filterableByValue = isFilterableByCellValue();

    const showCopyCellBtn = !controlCell && enabledCell;
    const showPasteCutDeleteCellBtns = !controlCell && editableCell && enabledCell;
    const showRegenerateCellBtn = !controlCell && regenerableCell && enabledCell;
    const showDeleteRowBtn = deletableRow;
    const showFilterByCellValueBtn = !controlCell && filterableByValue && enabledCell;
</script>

{#if showFilterByCellValueBtn}
    <KpContextMenuButton icon="filter" on:click={handleFilterByCellValue}>
        Filtrovat dle hodnoty
    </KpContextMenuButton>
{/if}

{#if showCopyCellBtn}
    <KpContextMenuButton icon="copy" on:click={handleCopyCell}>
        Zkopírovat buňku
    </KpContextMenuButton>
{/if}

{#if showPasteCutDeleteCellBtns}
    <KpContextMenuButton icon="paste" on:click={handlePasteCell}>
        Vložit buňku
    </KpContextMenuButton>

    <KpContextMenuButton icon="scissors" on:click={handleCutCell}>
        Vyjmout buňku
    </KpContextMenuButton>
{/if}

{#if showRegenerateCellBtn}
    <KpContextMenuButton icon="rotate-square" on:click={handleRegenerateCell}>
        Regenerovat buňku
    </KpContextMenuButton>
{/if}

{#if showPasteCutDeleteCellBtns}
    <KpContextMenuButton buttonStyle="danger" icon="trash" on:click={handleDeleteCell}>
        Smazat buňku
    </KpContextMenuButton>
{/if}

{#if showCopyCellBtn || showPasteCutDeleteCellBtns || showRegenerateCellBtn || showFilterByCellValueBtn}
    <KpHorizontalSeparator width="100%"/>
{/if}

<KpContextMenuButton icon="duplicate" on:click={handleDuplicateRow}>
    Duplikovat řádek
</KpContextMenuButton>

{#if showDeleteRowBtn}
    <KpContextMenuButton buttonStyle="danger" icon="trash" on:click={handleDeleteRow}>
        Smazat řádek
    </KpContextMenuButton>
{/if}